import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Grid, IconButton, Link, Menu, MenuItem, Switch, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { FormProvider, RHFSearchableSelectField, RHFSelectField, RHFTextField } from '../../common/hook-form'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { useSnackbar } from 'notistack'
import { saveBlob } from '../freight-profitability/freightPnlUtils'
import { errorText } from './shopifyEddLocationApi'

const PIN = /^\d{6}$/
const PAGE_SIZES = [50, 100, 500]
const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')
const lc = (s) => String(s ?? '').trim().toLowerCase()

const MATCH_TEXT = {
  PINCODE: 'pincode rule',
  STATE: 'state rule',
  DEFAULT: 'default pickup, no rule matched',
  NONE: 'no rule matched and no default pickup',
  DISABLED: 'switched off',
  NOT_CONFIGURED: 'nothing configured yet',
}

const HOW_IT_WORKS = 'The merchant\'s store (for example Shopify) asks which warehouse ships to a customer pincode so it can quote a delivery date on the right lane. '
  + 'A pincode is resolved in this order: its own pincode row, then its state row, then the default pickup pincode. '
  + 'With one warehouse, set only the default. This never changes how orders are allocated.'

const Count = ({ label, value, tone }) => (
  <Box sx={{ flex: '1 1 110px', p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: tone }}>{(value || 0).toLocaleString('en-IN')}</Typography>
  </Box>
)

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
const downloadErrors = (rows) => {
  const lines = ['Row,Drop,Pickup Pincode,Reason', ...rows.map((r) => [r.row, r.drop, r.pickupPincode, r.reason].map(csvCell).join(','))]
  saveBlob(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }), 'pickup_drop_mapping_upload_errors.csv')
}

const btn = { textTransform: 'none' }
const rowSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }

/** What Shopify gets right now, in one chip. */
const statusOf = (config, rules) => {
  if (!config) return null
  if (config.saved === false) return { label: 'Not configured', color: 'default' }
  if (config.enabled === false) return { label: 'Disabled', color: 'warning' }
  const def = config.defaultPickupPincode
  const label = `Live · ${def ? `default ${def}` : 'no default'} · ${rules} rule${rules === 1 ? '' : 's'}`
  return { label, color: def || rules ? 'success' : 'warning' }
}

/**
 * Shopify EDD Pickup Mapping for one merchant, as one flat card (same shape as the PTL pages): a toolbar row, a
 * settings + tester row, then the rules table. `header` is an optional node for the toolbar's left side (the admin
 * panel puts its merchant picker there); with `api` null only the header and a prompt render.
 * Admin-panel copy of proship-web's ShopifyEddLocationPanel: `api` comes from apiFor(merchantOid).
 */
export default function ShopifyEddLocationPanel({ api, header }) {
  const A = useMemo(() => api || null, [api])
  const { enqueueSnackbar } = useSnackbar()

  const [config, setConfig] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [states, setStates] = useState([])
  const [pickupHints, setPickupHints] = useState([])

  const [enabled, setEnabled] = useState(true)
  const [defaultPickup, setDefaultPickup] = useState('')
  const [savingSettings, setSavingSettings] = useState(false)

  const [filter, setFilter] = useState('')
  const [showMissing, setShowMissing] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [editing, setEditing] = useState(null) // { drop, value }
  const [addOpen, setAddOpen] = useState(false)
  const [addInitialDrop, setAddInitialDrop] = useState('')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [clearOpen, setClearOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [busyRow, setBusyRow] = useState('')
  const [downloading, setDownloading] = useState('')

  const [testPin, setTestPin] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [testError, setTestError] = useState('')

  const applyConfig = useCallback((c) => {
    setConfig(c)
    setEnabled(c?.enabled !== false)
    setDefaultPickup(c?.defaultPickupPincode || '')
  }, [])

  const load = useCallback(() => {
    if (!A) return
    setLoadError('')
    A.fetchConfig().then(applyConfig).catch((e) => setLoadError(errorText(e, 'Could not load the config')))
    A.fetchStates().then(setStates).catch(() => setStates([]))
    A.fetchPickupPincodes().then(setPickupHints).catch(() => setPickupHints([]))
  }, [applyConfig, A])

  useEffect(() => { load() }, [load])

  // ── derived ───────────────────────────────────────────────────────────────
  const mappings = useMemo(() => config?.mappings || [], [config])
  const stateRows = useMemo(() => mappings.filter((m) => m.dropType === 'STATE'), [mappings])
  const pinRows = mappings.length - stateRows.length
  const status = statusOf(config, mappings.length)
  const missingStates = useMemo(() => {
    const mapped = new Set(stateRows.map((m) => lc(m.dropValue)))
    return states.filter((s) => !mapped.has(lc(s)))
  }, [states, stateRows])
  const visible = useMemo(() => {
    const q = lc(filter)
    if (!q) return mappings
    return mappings.filter((m) => lc(m.dropValue).includes(q) || String(m.pickupPincode).includes(q))
  }, [mappings, filter])
  const pageRows = useMemo(() => visible.slice(page * pageSize, page * pageSize + pageSize), [visible, page, pageSize])
  const highlightDrop = testResult?.matchedDrop ? lc(testResult.matchedDrop) : null

  useEffect(() => { setPage(0) }, [filter, pageSize])

  // Jump to the page holding the row the tester matched.
  useEffect(() => {
    if (!highlightDrop) return
    const idx = visible.findIndex((m) => lc(m.dropValue) === highlightDrop)
    if (idx >= 0) setPage(Math.floor(idx / pageSize))
  }, [highlightDrop, visible, pageSize])

  // ── settings: each control saves itself ───────────────────────────────────
  const defaultPickupBad = defaultPickup !== '' && !PIN.test(defaultPickup)
  const defaultPickupDirty = !!config && !defaultPickupBad && (defaultPickup || '') !== (config.defaultPickupPincode || '')

  const saveSetting = async (patch) => {
    setSavingSettings(true)
    try {
      applyConfig(await A.saveSettings(patch))
    } catch (e) {
      applyConfig(config) // roll the controls back to the saved values
      enqueueSnackbar(errorText(e, 'Could not save'), { variant: 'error' })
    } finally {
      setSavingSettings(false)
    }
  }
  const onToggleEnabled = (e) => {
    setEnabled(e.target.checked)
    saveSetting({ enabled: e.target.checked })
  }
  const commitDefaultPickup = () => {
    if (defaultPickupDirty) saveSetting({ defaultPickupPincode: defaultPickup || null })
  }

  // ── rules ─────────────────────────────────────────────────────────────────
  const onDelete = async (row) => {
    setBusyRow(row.dropValue)
    try {
      applyConfig(await A.deleteMapping(row.dropValue))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not remove the row'), { variant: 'error' })
    } finally {
      setBusyRow('')
    }
  }

  const commitEdit = async () => {
    if (!editing) return
    const { drop, value } = editing
    const row = mappings.find((m) => m.dropValue === drop)
    setEditing(null)
    if (!row || !PIN.test(value) || value === row.pickupPincode) return
    setBusyRow(drop)
    try {
      applyConfig(await A.addMapping({ drop, pickupPincode: value }))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not change the pickup'), { variant: 'error' })
    } finally {
      setBusyRow('')
    }
  }

  const onClear = async () => {
    setClearOpen(false)
    try {
      applyConfig(await A.clearMappings())
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not clear the rows'), { variant: 'error' })
    }
  }

  const openAdd = (initialDrop = '') => { setAddInitialDrop(initialDrop); setAddOpen(true) }

  const download = async (kind) => {
    setDownloading(kind)
    try {
      const res = await (kind === 'template' ? A.downloadTemplate() : A.downloadMappings())
      saveBlob(res.data, kind === 'template' ? 'pickup_drop_mapping_template.csv' : 'pickup_drop_mapping.csv')
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed'), { variant: 'error' })
    } finally {
      setDownloading('')
    }
  }

  // ── tester: runs itself once six digits are typed ─────────────────────────
  useEffect(() => {
    if (!A) return undefined
    if (!PIN.test(testPin)) { setTestResult(null); setTestError(''); return undefined }
    const t = setTimeout(() => {
      setTesting(true)
      setTestError('')
      A.resolvePickup(testPin)
        .then(setTestResult)
        .catch((e) => { setTestResult(null); setTestError(errorText(e, 'Lookup failed')) })
        .finally(() => setTesting(false))
    }, 400)
    return () => clearTimeout(t)
  }, [testPin, A, config])

  return (
    <Card sx={{ boxShadow: 'none', border: 1, borderColor: 'divider' }}>
      {/* ── Filter strip: who + current state + lookup ─────────────────── */}
      <Box sx={{ ...rowSx, bgcolor: 'action.hover' }}>
        {header}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {status && <Chip size='small' label={status.label} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} sx={{ fontSize: 12 }} />}
          <Tooltip title={HOW_IT_WORKS} placement='bottom-start'>
            <IconButton size='small' aria-label='How it works' sx={{ p: 0.25 }}><HelpOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></IconButton>
          </Tooltip>
          {A && config?.lastUploadAt && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>uploaded {fmt(config.lastUploadAt)}</Typography>
          )}
        </Box>
        {A && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {testError && <Typography sx={{ fontSize: 12, color: 'error.main' }}>{testError}</Typography>}
            {testResult && !testing && (
              <Typography sx={{ fontSize: 12, color: testResult.found ? 'success.dark' : 'warning.dark' }}>
                {testResult.dropState ? `${testResult.dropState} · ` : ''}
                {testResult.found ? <>pickup <b>{testResult.pickupPincode}</b></> : <b>no pickup</b>}
                {' · '}{MATCH_TEXT[testResult.matchedBy] || testResult.matchedBy}
              </Typography>
            )}
            <TextField size='small' placeholder='Test a pincode' value={testPin} onChange={(e) => setTestPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              sx={{ width: 190, flexShrink: 0, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <SearchIcon fontSize='small' sx={{ mr: 1, color: testing ? 'primary.main' : 'text.disabled' }} /> }} />
          </Box>
        )}
      </Box>

      {!A ? (
        <Typography sx={{ px: 2, py: 4, fontSize: 13, color: 'text.secondary', textAlign: 'center' }}>Select a merchant to view or edit their mapping.</Typography>
      ) : (
        <>
          {loadError && <Alert severity='error' sx={{ mx: 2, mt: 1.5, fontSize: 13 }}>{loadError}</Alert>}

          {/* ── Toolbar: default pickup + actions ──────────────────────── */}
          <Box sx={rowSx}>
            <Autocomplete
              freeSolo
              options={pickupHints}
              inputValue={defaultPickup}
              onInputChange={(_, v) => setDefaultPickup((v || '').replace(/\D/g, '').slice(0, 6))}
              sx={{ width: 220 }}
              onBlur={commitDefaultPickup}
              onChange={(_, v, reason) => { if (reason === 'selectOption' && v) saveSetting({ defaultPickupPincode: v }) }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDefaultPickup() } }}
              disabled={savingSettings}
              renderInput={(params) => (
                <TextField {...params} size='small' label='Default pickup pincode' error={defaultPickupBad}
                  helperText={defaultPickupBad ? 'Must be 6 digits' : defaultPickupDirty ? 'Enter to save' : undefined}
                  FormHelperTextProps={{ sx: { position: 'absolute', bottom: -18, m: 0 } }} />
              )} />
            <FormControlLabel control={<Switch size='small' checked={enabled} disabled={savingSettings} onChange={onToggleEnabled} />}
              label={<Typography sx={{ fontSize: 13 }}>Enabled</Typography>} sx={{ ml: 0 }} />
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size='small' variant='contained' startIcon={<UploadFileOutlinedIcon />} onClick={() => setUploadOpen(true)} sx={btn}>Upload CSV</Button>
              <Button size='small' variant='outlined' startIcon={<AddIcon />} onClick={() => openAdd()} sx={btn}>Add row</Button>
              <Tooltip title='More'>
                <IconButton size='small' aria-label='More actions' onClick={(e) => setMenuAnchor(e.currentTarget)}><MoreVertIcon fontSize='small' /></IconButton>
              </Tooltip>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem disabled={downloading === 'template'} onClick={() => { setMenuAnchor(null); download('template') }}>Download template</MenuItem>
                <MenuItem disabled={!mappings.length || downloading === 'current'} onClick={() => { setMenuAnchor(null); download('current') }}>Download current rules</MenuItem>
                <MenuItem disabled={!mappings.length} onClick={() => { setMenuAnchor(null); setClearOpen(true) }} sx={{ color: 'error.main' }}>Clear all rules…</MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* ── Rules ──────────────────────────────────────────────────── */}
          {mappings.length === 0 ? (
            <Typography sx={{ px: 2, py: 4, fontSize: 13, color: 'text.secondary', textAlign: 'center' }}>
              No rules yet, so every customer pincode uses the default pickup pincode. Ship from more than one warehouse?{' '}
              <Link component='button' onClick={() => download('template')} sx={{ fontSize: 13, verticalAlign: 'baseline' }}>Download the template</Link>, fill it in and upload it.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
                <TextField size='small' placeholder='Filter by state or pincode' value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ width: 260 }}
                  InputProps={{ startAdornment: <SearchIcon fontSize='small' sx={{ mr: 1, color: 'text.disabled' }} /> }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{stateRows.length} state · {pinRows} pincode</Typography>
                {states.length > 0 && (
                  <Chip size='small' variant={showMissing ? 'filled' : 'outlined'} color={missingStates.length ? 'warning' : 'success'}
                    label={missingStates.length ? `${missingStates.length} of ${states.length} states have no rule` : 'Every state has a rule'}
                    onClick={missingStates.length ? () => setShowMissing((v) => !v) : undefined} sx={{ fontSize: 12 }} />
                )}
              </Box>
              {showMissing && missingStates.length > 0 && (
                <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', width: '100%' }}>These states fall back to the default pickup. Click one to add its rule.</Typography>
                  {missingStates.map((s) => <Chip key={s} size='small' variant='outlined' label={s} onClick={() => openAdd(s)} sx={{ fontSize: 12 }} />)}
                </Box>
              )}
              {visible.length === 0 ? (
                <Typography sx={{ px: 2, pb: 2, fontSize: 13, color: 'text.secondary' }}>No rows match the filter.</Typography>
              ) : (
                <>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 110 }}>Type</TableCell>
                        <TableCell>Drop</TableCell>
                        <TableCell sx={{ width: 180 }}>Pickup Pincode</TableCell>
                        <TableCell sx={{ width: 56 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((m) => {
                        const isEditing = editing?.drop === m.dropValue
                        const hit = highlightDrop && lc(m.dropValue) === highlightDrop
                        return (
                          <TableRow key={`${m.dropType}-${m.dropValue}`} hover selected={!!hit}>
                            <TableCell><Chip size='small' label={m.dropType === 'STATE' ? 'State' : 'Pincode'} color={m.dropType === 'STATE' ? 'primary' : 'default'} variant='outlined' /></TableCell>
                            <TableCell sx={{ fontSize: 13 }}>{m.dropValue}{hit ? <Typography component='span' sx={{ fontSize: 11, color: 'success.dark', ml: 1 }}>matched</Typography> : null}</TableCell>
                            <TableCell sx={{ fontSize: 13, py: isEditing ? 0.5 : undefined }}>
                              {isEditing ? (
                                <TextField size='small' autoFocus value={editing.value} sx={{ width: 120 }}
                                  onChange={(e) => setEditing({ drop: m.dropValue, value: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                                  onBlur={commitEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null) }}
                                  error={editing.value !== '' && !PIN.test(editing.value)}
                                  inputProps={{ 'aria-label': `Pickup pincode for ${m.dropValue}` }} />
                              ) : (
                                <Tooltip title='Click to change' placement='right'>
                                  <Box component='span' onClick={() => busyRow !== m.dropValue && setEditing({ drop: m.dropValue, value: m.pickupPincode })}
                                    sx={{ cursor: 'text', borderBottom: '1px dashed', borderColor: 'divider', pb: 0.25 }}>
                                    {m.pickupPincode}
                                  </Box>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title='Remove'>
                                <span><IconButton size='small' disabled={busyRow === m.dropValue} onClick={() => onDelete(m)}><DeleteOutlineIcon fontSize='small' /></IconButton></span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  {visible.length > PAGE_SIZES[0] && (
                    <TablePagination component='div' count={visible.length} page={page} rowsPerPage={pageSize} rowsPerPageOptions={PAGE_SIZES}
                      onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
                      labelRowsPerPage='Rows' sx={{ borderTop: 1, borderColor: 'divider' }} />
                  )}
                </>
              )}
            </>
          )}

          <AddRowDialog open={addOpen} initialDrop={addInitialDrop} onClose={() => setAddOpen(false)} states={states} pickupHints={pickupHints} api={A}
            onSaved={(c) => { applyConfig(c); setAddOpen(false) }} />
          <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} api={A} existing={mappings.length}
            onDone={() => { setUploadOpen(false); load() }} onTemplate={() => download('template')} />
          <Dialog open={clearOpen} onClose={() => setClearOpen(false)} maxWidth='xs' fullWidth>
            <DialogTitle sx={{ fontSize: 16 }}>Remove all {mappings.length} rows?</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: 13 }}>The default pickup pincode stays, so the store keeps getting that for every customer pincode.</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setClearOpen(false)} sx={btn}>Cancel</Button>
              <Button color='error' variant='contained' onClick={onClear} sx={btn}>Remove all</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Card>
  )
}

const RULE_TYPES = [
  { value: 'STATE', label: 'A whole state' },
  { value: 'PINCODE', label: 'One customer pincode' },
]

const ruleSchema = Yup.object().shape({
  ruleType: Yup.string().required('Choose what the rule applies to'),
  state: Yup.string().when('ruleType', { is: 'STATE', then: (s) => s.required('Pick a state') }),
  dropPincode: Yup.string().when('ruleType', { is: 'PINCODE', then: (s) => s.matches(PIN, 'Customer pincode must be 6 digits') }),
  pickupPincode: Yup.string().matches(PIN, 'Pickup pincode must be 6 digits'),
})

/** Add one rule. Opened from the toolbar, or from a state chip with that state pre-selected. */
function AddRowDialog({ open, initialDrop, onClose, states, pickupHints, api, onSaved }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const methods = useForm({
    resolver: yupResolver(ruleSchema),
    defaultValues: { ruleType: 'STATE', state: '', dropPincode: '', pickupPincode: '' },
  })
  const { handleSubmit, reset, watch, setValue } = methods
  const ruleType = watch('ruleType')
  const stateOptions = useMemo(() => states.map((s) => ({ value: s, label: s })), [states])

  useEffect(() => {
    if (!open) return
    const preset = initialDrop && states.some((s) => lc(s) === lc(initialDrop)) ? initialDrop : ''
    reset({ ruleType: 'STATE', state: preset, dropPincode: '', pickupPincode: '' })
    setError('')
  }, [open, initialDrop, states, reset])

  const close = () => { if (!busy) onClose() }

  const onSubmit = async (values) => {
    setBusy(true)
    setError('')
    try {
      const drop = values.ruleType === 'STATE' ? values.state : values.dropPincode
      onSaved(await api.addMapping({ drop, pickupPincode: values.pickupPincode }))
    } catch (e) {
      setError(errorText(e, 'Could not save the rule'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Add a rule</DialogTitle>
      <DialogContent>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <RHFSelectField name='ruleType' label='Applies to' options={RULE_TYPES} width='100%' />
            </Grid>
            <Grid item xs={12}>
              {ruleType === 'STATE'
                ? <RHFSearchableSelectField name='state' label='State' options={stateOptions} fullWidth placeholder='Pick a state' />
                : <RHFTextField name='dropPincode' label='Customer pincode' inputProps={{ maxLength: 6, inputMode: 'numeric' }} />}
            </Grid>
            <Grid item xs={12}>
              <RHFTextField name='pickupPincode' label='Pickup (warehouse) pincode' inputProps={{ maxLength: 6, inputMode: 'numeric' }} />
              {pickupHints.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Merchant's pickup locations:</Typography>
                  {pickupHints.slice(0, 8).map((p) => (
                    <Chip key={p} size='small' variant='outlined' label={p} onClick={() => setValue('pickupPincode', p, { shouldValidate: true })} sx={{ fontSize: 11, height: 22 }} />
                  ))}
                </Box>
              )}
            </Grid>
            {error && <Grid item xs={12}><Alert severity='error' sx={{ fontSize: 13 }}>{error}</Alert></Grid>}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={close} disabled={busy} sx={btn}>Cancel</Button>
              <LoadingButton type='submit' variant='contained' loading={busy} sx={btn}>Save rule</LoadingButton>
            </Grid>
          </Grid>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

/** Check the file first (nothing saved), see what it will change, then confirm with the same file. */
function UploadDialog({ open, onClose, onDone, onTemplate, api, existing }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const { enqueueSnackbar } = useSnackbar()

  const reset = () => { setFile(null); setPreview(null); setError('') }
  const close = () => { if (!busy) { reset(); onClose() } }
  const pick = (e) => { setFile(e.target.files?.[0] || null); setPreview(null); setError(''); e.target.value = '' }

  const run = async (confirm) => {
    setBusy(true)
    setError('')
    try {
      const res = await api.uploadMappings({ file, confirm })
      if (!confirm) {
        setPreview(res)
      } else {
        enqueueSnackbar(`Saved ${res.validRows} row${res.validRows === 1 ? '' : 's'}`, { variant: 'success' })
        if (res.invalidRows?.length) downloadErrors(res.invalidRows)
        reset()
        onDone()
      }
    } catch (e) {
      setError(errorText(e, 'Upload failed'))
    } finally {
      setBusy(false)
    }
  }

  const invalid = preview?.invalidRows || []
  const noChange = preview && !preview.added && !preview.changed

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Upload Pickup–Drop Mapping</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, mb: 1 }}>
          Two columns: <b>Drop (State or Pincode)</b> and <b>Pickup Pincode</b>.{' '}
          <Link component='button' onClick={onTemplate} sx={{ fontSize: 13, verticalAlign: 'baseline' }}>Download the template</Link>, fill the pickup pincode
          against the states you ship to, add pincode rows for exceptions, then upload it here. Blank pickups are skipped.
          {existing ? <> Rows in the file are <b>added or updated</b>; the {existing} existing rule{existing === 1 ? '' : 's'} not in the file stay as they are.</> : null}
        </Typography>
        <Table size='small' sx={{ mb: 2, maxWidth: 520, '& td, & th': { fontSize: 12.5, py: 0.5 } }}>
          <TableHead>
            <TableRow><TableCell>Drop (State or Pincode)</TableCell><TableCell>Pickup Pincode</TableCell><TableCell>Meaning</TableCell></TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>Haryana</TableCell><TableCell>110001</TableCell><TableCell>All of Haryana ships from 110001</TableCell></TableRow>
            <TableRow><TableCell>122001</TableCell><TableCell>122002</TableCell><TableCell>Exception: this pincode ships from 122002</TableCell></TableRow>
            <TableRow><TableCell>Kerala</TableCell><TableCell><i>(blank)</i></TableCell><TableCell>Skipped; uses the default pickup</TableCell></TableRow>
          </TableBody>
        </Table>
        <Button component='label' variant='outlined' size='small' startIcon={<UploadFileOutlinedIcon />} sx={btn} disabled={busy}>
          {file ? file.name : 'Choose CSV file'}
          <input hidden type='file' accept='.csv' onChange={pick} />
        </Button>

        {preview && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Against what is live now</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Count label='Added' value={preview.added} tone='success.main' />
              <Count label='Pickup changed' value={preview.changed} tone='info.main' />
              <Count label='Unchanged' value={preview.unchanged} />
              <Count label='Blank (skipped)' value={preview.blankPickupRows} />
              <Count label='Errors' value={invalid.length} tone={invalid.length ? 'error.main' : undefined} />
            </Box>
            {noChange && <Alert severity='info' sx={{ mt: 1.5, fontSize: 13 }}>This file matches what is live. Confirming changes nothing.</Alert>}
            {invalid.length > 0 && (
              <Alert severity='error' sx={{ mt: 1.5, fontSize: 13 }}
                action={<Button size='small' color='inherit' onClick={() => downloadErrors(invalid)} sx={btn}>Download</Button>}>
                {invalid.length} row{invalid.length > 1 ? 's' : ''} will be skipped. First: row {invalid[0].row}, {invalid[0].reason}
              </Alert>
            )}
          </Box>
        )}
        {error && <Alert severity='error' sx={{ mt: 2, fontSize: 13 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy} sx={btn}>Cancel</Button>
        {!preview ? (
          <LoadingButton variant='contained' disabled={!file} loading={busy} onClick={() => run(false)} sx={btn}>Check file</LoadingButton>
        ) : (
          <LoadingButton variant='contained' loading={busy} disabled={noChange} onClick={() => run(true)} sx={btn}>Confirm upload</LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
